import Handlebars from '@sparta.global.vendors/handlebars';
import { messagingHelper } from '@sparta.global.helpers/messaging-helper/dist/es6.js';
import spartaGlobalParams from '@sparta.global.utilities/sparta-global-params-utility/dist/es6.js';

/**
 * Gets spinner wrapper
 * @returns {string} Markup string
 */
function getSpinner() {
  return '<div class="spa-icon-loading icon-loader-72"></div>';
}

/**
 * Creats markup for cancel/retry triggers
 * @param {*} controlSettings object containing strings and jcr inputs
 * @returns {void}
 */
function spinnerControls(controlSettings) {
  return `
  <div class="spa-spin-cancel-wrapper">
    <button
      class="spa-btn spa-btn--small spa-btn--link spa-spin-cancel-btn ${controlSettings.cancelDisabled ? 'spa-spin--hidden' : ''}"
      type="button"
      data-override="${controlSettings.jcrValue.cancelBtn}"
    >
      ${controlSettings.cancelBtn}
    </button>

    <button
      class="spa-btn spa-btn--small spa-btn--link spa-spin-retry-btn spa-spin--hidden ${controlSettings.retryDisabled ? 'spin-retry-disabled' : ''}"
      type="button"
      data-override="${controlSettings.jcrValue.retryBtn}"
    >
      ${controlSettings.retryBtn}
    </button>
  </div>
  `.trim();
}

/**
 * Generate messaging utility HTML based on provided data.
 * @param {*} title message title
 * @param {*} content message content
 * @param {*} type message type
 * @returns {void}
*/
function getMsgHelperMarkup(title, content, type) {
  return `
    ${messagingHelper({
    title,
    content,
    type,
    options: {
      size: 'small',
      border: 0
    }
  })}
  `;
}

/**
   * @method helpers.spinner
   * @param {object} jcr JSON object used to build the spinner.
   * @returns {Node} `Handlebars.SafeString` version of `spinner` element.
   * @description Creates spinner from options provided.
   */
function spinner(jcr, args) {
  if (!jcr) {
    throw new Error('No spinner JCR objects provided to helper-spinner.');
  }
  // Set defaults if JCR options do not exist.
  const pageLevel = jcr.pageLevel ? jcr.pageLevel === true : false;
  const loadingMessage = jcr.loadingMessage ? jcr.loadingMessage : '';
  const errorTitle = jcr.errorTitle ? jcr.errorTitle : '';
  const errorMessage = jcr.errorMessage ? jcr.errorMessage : '';
  const cancelTitle = jcr.cancelTitle ? jcr.cancelTitle : '';
  const cancelMessage = jcr.cancelMessage ? jcr.cancelMessage : '';
  const cancelDisabled = jcr.cancelDisabled ? jcr.cancelDisabled : false;
  const retryDisabled = jcr.retryDisabled ? jcr.retryDisabled : false;
  const maxWidth = jcr.maxWidth ? `${jcr.maxWidth}` : '';
  const globalStrings = spartaGlobalParams.getSpaParams({
    context: args,
    lookupKey: 'spinner',
    defaultObj: {
      "cancelBtn": "Cancel",
      "retryBtn": "Try again",
      "initMessage": "Loading"
    }
  });
  const initMessageVisible = jcr.initMessageVisible || false;
  const initMessage = jcr.initMessage || '';
  const screenReaderLoadingMsg = initMessage !== '' ? initMessage : globalStrings.initMessage;
  const errorSkin = jcr.skin ? `data-spinner-skin="${jcr.skin}"` : '';
  const cancelBtn = jcr.cancelBtn ? jcr.cancelBtn : globalStrings.cancelBtn;
  const retryBtn = jcr.retryBtn ? jcr.retryBtn : globalStrings.retryBtn;
  let classNames = 'spa-card spa-card--medium spa-card--overlay spa-spin spa-spin--hidden';
  classNames = `
    ${classNames}
    ${pageLevel ? 'spa-spin-page-wrapper' : ''}
    ${jcr.className || ''}
    ${jcr.removeSpinnerShadow ? 'spa-spinner--remove-shadow' : ''}
    ${jcr.skin ? `spa-spinner--has-skin spa-spinner-skin--${jcr.skin}` : ''}
  `.trim();
  const controlSettings = {
    cancelDisabled,
    cancelBtn,
    retryDisabled,
    retryBtn,
    jcrValue: {
      cancelBtn: !('cancelBtn' in jcr),
      retryBtn: !('retryBtn' in jcr)
    }
  };

  // if page level center container in the middle of the page and define widths
  let spinner = `<div role="status" class="${classNames}" ${maxWidth ? `data-max-width="${maxWidth}"` : ''}>
    <div class="spa-spin-wrapper" ${errorSkin}>
      <div class="spa-spin-content-wrapper">
        <div class="spa-spin-load-msg body-small">
          ${getSpinner()}
          <div class="spa-spin-msg-wrapper spa-spin--invisible ${loadingMessage === '' ? 'spa-spin--hidden' : ''}">
            ${loadingMessage}
          </div>
        </div>
        <div class="spa-spin-msg-init body-small ${initMessageVisible ? '' : 'ada-hidden'}" data-init-msg="${screenReaderLoadingMsg}" role="alert">
        </div>

        <div class="spa-spin-cancel-msg spa-spin--hidden">
          ${jcr.skin ? `${getMsgHelperMarkup(cancelTitle, cancelMessage, jcr.skin)}` : `
              <div class="spa-spin-cancel-title headline-xsmall">
                ${cancelTitle}
              </div>
              <div class="spa-spin-cancel-text body-small">
                ${cancelMessage}
              </div>
            `.trim()}
        </div>

        <div class="spa-spin-error-msg spa-spin--hidden">
          ${jcr.skin ? `${getMsgHelperMarkup(errorTitle, errorMessage, jcr.skin)}` : `
              <div class="spa-spin-error-icon">
                <div class="spa-icon icon-warning-red"></div>
              </div>
              <div class="spa-spin-error-title headline-xsmall">
                ${errorTitle}
              </div>
              <div class="spa-spin-error-text body-small">
                ${errorMessage}
            </div>
            `.trim()}
        </div>
      </div>

      ${spinnerControls(controlSettings)}

    </div>
  </div>`.trim();

  // closing tags for page level spinner
  if (pageLevel) spinner += '</div>';

  // trim white space
  spinner.trim();
  return new Handlebars.SafeString(spinner);
}

/**
 * @class spinnerHelper
 * @description Class used to register helper with Handlebars.
 */

export { spinner };
